import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

export interface UserCredentials {
  email: string
  password: string
  remember_me: boolean
}

export type Account = any;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isAuth: boolean = false;

  private readonly ghostUserObjectName = 'fiGalaxyGhostUser';
  private readonly ghostTokenObjectName = 'fiGalaxyGhostToken';
  private readonly isGhostSessionKey = 'fiGalaxyIsGhostSession';
  
  // Backup keys for main session
  private readonly backupUserTypeKey = 'fiGalaxyBackupUserType';
  private readonly backupTokenKey = 'fiGalaxyBackupToken';
  private readonly backupUserObjKey = 'fiGalaxyBackupUserObj';
  private readonly backupPermissionsKey = 'fiGalaxyBackupPermissions';
  private readonly backupUrlKey = 'fiGalaxyBackupUrl';
  private readonly sessionSwitchingKey = 'fiGalaxySessionSwitching';

  // Cookies are mirrored for same-host readers only. They must not act as a
  // session timer — localStorage holds the session, and only a backend 401
  // (see ErrorCodesInterceptor) logs the user out.
  private static readonly SESSION_COOKIE_DAYS = 36500;

  private readonly knownUserTypes = [
    'Staff',
    'Agents',
    'Brokers',
    'Producers',
    'Dealers',
    'Onboard',
    'Central',
    'centralThirdParties',
  ] as const;

  set userCredentials(userCredentials: UserCredentials | null) {
    localStorage.setItem('userCredentials', JSON.stringify(userCredentials));
  }

  get userCredentials(): UserCredentials | null {
    const userCredentials: string | null = localStorage.getItem('userCredentials');
    if (!userCredentials) {
      return null;
    }

    return JSON.parse(userCredentials) as UserCredentials;
  }

  // Ghost session methods (use localStorage for same-tab replacement)
  isGhostSession(): boolean {
    return localStorage.getItem(this.isGhostSessionKey) === 'true';
  }

  saveGhostUser(user: any): void {
    localStorage.setItem(this.ghostUserObjectName, JSON.stringify(user));
    if (user?.access_token) {
      localStorage.setItem(this.ghostTokenObjectName, user.access_token);
    }
    localStorage.setItem(this.isGhostSessionKey, 'true');
    this.isAuth = true;
  }

  removeGhostUser(): void {
    localStorage.removeItem(this.ghostUserObjectName);
    localStorage.removeItem(this.ghostTokenObjectName);
    localStorage.removeItem(this.isGhostSessionKey);
  }

  // Backup and restore main session for ghost login
  backupMainSession(currentUrl?: string): void {
    const userType = this.getUserType();
    const token = this.getUserToken();
    const userObj = this.getUserObj();
    const permissions = localStorage.getItem('permissions');

    if (userType) {
      localStorage.setItem(this.backupUserTypeKey, userType);
    }
    if (token) {
      localStorage.setItem(this.backupTokenKey, token);
    }
    if (userObj) {
      localStorage.setItem(this.backupUserObjKey, JSON.stringify(userObj));
    }
    if (permissions) {
      localStorage.setItem(this.backupPermissionsKey, permissions);
    }
    if (currentUrl) {
      localStorage.setItem(this.backupUrlKey, currentUrl);
    }
  }

  restoreMainSession(): string | null {
    // Remove ghost session data
    this.removeGhostUser();

    // Restore backup data
    const backupUserType = localStorage.getItem(this.backupUserTypeKey);
    const backupToken = localStorage.getItem(this.backupTokenKey);
    const backupUserObj = localStorage.getItem(this.backupUserObjKey);
    const backupPermissions = localStorage.getItem(this.backupPermissionsKey);
    const backupUrl = localStorage.getItem(this.backupUrlKey);

    if (backupUserType) {
      this.setUserType(backupUserType, false);

      if (backupToken) {
        this.setUserToken(backupToken, false);
      }

      if (backupUserObj) {
        this.setUserObj(this.parseJson(backupUserObj, {}), false);
      }
    }

    if (backupPermissions) {
      localStorage.setItem('permissions', backupPermissions);
    }

    // Clear backup data
    localStorage.removeItem(this.backupUserTypeKey);
    localStorage.removeItem(this.backupTokenKey);
    localStorage.removeItem(this.backupUserObjKey);
    localStorage.removeItem(this.backupPermissionsKey);
    localStorage.removeItem(this.backupUrlKey);

    this.isAuth = true;
    
    return backupUrl;
  }

  hasBackupSession(): boolean {
    return !!localStorage.getItem(this.backupUserTypeKey);
  }

  setSessionSwitching(switching: boolean): void {
    if (switching) {
      localStorage.setItem(this.sessionSwitchingKey, 'true');
    } else {
      localStorage.removeItem(this.sessionSwitchingKey);
    }
  }

  isSessionSwitching(): boolean {
    return localStorage.getItem(this.sessionSwitchingKey) === 'true';
  }

  constructor(private router: Router) {}

  setUserType(userType: string, _isRememberMe: boolean = false) {
    const value = userType ? userType : '';
    localStorage.setItem('fi_galaxy_user_type', value);
    this.setCookie(
      'fi_galaxy_user_type',
      value,
      AuthService.SESSION_COOKIE_DAYS
    );
  }

  getUserType(): string | null {
    if (this.isGhostSession()) {
      const user: any = this.getUserObj();
      // Ensure we check for nested user object from legacy save or flat object
      return user?.user_type || user?.role || user?.user?.user_type || '';
    }
    const fromStorage = localStorage.getItem('fi_galaxy_user_type');
    if (fromStorage) {
      return fromStorage;
    }
    const fromCookie = this.getCookie('fi_galaxy_user_type');
    if (fromCookie) {
      localStorage.setItem('fi_galaxy_user_type', fromCookie);
      return fromCookie;
    }
    return this.recoverUserTypeFromStoredToken();
  }

  setUserToken(token: any, _isRememberMe: boolean = false) {
    const userType: string | null = this.getUserType();
    localStorage.setItem(`fiGalaxyToken${userType}`, token);
    this.setCookie(
      `fiGalaxyToken${userType}`,
      token,
      AuthService.SESSION_COOKIE_DAYS
    );
    this.isAuth = true;
    this.checkUserAuth();
  }

  setUserObj(userObj: any, _isRememberMe: boolean = false) {
    const userType: string | null = this.getUserType();
    const serialized = JSON.stringify(userObj);
    localStorage.setItem(`fiGalaxyObj${userType}`, serialized);
    this.setCookie(
      `fiGalaxyObj${userType}`,
      serialized,
      AuthService.SESSION_COOKIE_DAYS
    );
  }

  getUserObjFromCookie(): any {
    const userType: string | null = this.getUserType();
    const value = this.getCookie(`fiGalaxyObj${userType}`);
    if (!value) {
      return value;
    }
    let res = value;
    try {
      res = JSON.parse(value);
    } catch (e) {}
    return res;
  }

  isUserAdmin() {
    let userObj =
      JSON.parse(
        JSON.parse(JSON.stringify(localStorage.getItem('fiGalaxyObj'))),
      ) || null;
    return userObj && userObj.role == 'owner' ? true : false;
  }

  checkUserAuth() {
    if (this.getUserToken()) {
      this.isAuth = true;
      const userObj: any = this.getUserObj();
    }
  }

  signOut() {
    const userType: string | null = this.getUserType();
    if (localStorage?.getItem(`fiGalaxyToken${userType}`)) {
      localStorage.removeItem(`fiGalaxyToken${userType}`);
    } else {
      sessionStorage.removeItem(`fiGalaxyToken${userType}`);
    }
    localStorage.removeItem(`fiGalaxyObj${userType}`);
    localStorage.removeItem(`fi_galaxy_user_type`);
    localStorage.removeItem(`permissions`);
    localStorage.removeItem('progressBarInfo');
    
    // Clean up ghost session and backup data
    this.removeGhostUser();
    localStorage.removeItem(this.backupUserTypeKey);
    localStorage.removeItem(this.backupTokenKey);
    localStorage.removeItem(this.backupUserObjKey);
    localStorage.removeItem(this.backupPermissionsKey);
    localStorage.removeItem(this.sessionSwitchingKey);

    // in case login by set cookies
    this.deleteCookie(`fiGalaxyToken${userType}`);
    this.deleteCookie(`fiGalaxyObj${userType}`);
    this.deleteCookie(`fi_galaxy_user_type`);
    this.deleteCookie('organizationsList');
    this.deleteCookie('notificationToken');
    this.deleteCookie('fiGalaxyTokencentralThirdParties');
    // this.deleteCookie(`permissions`);

    this.isAuth = false;
    this.router.navigate(['/auth/sign-in']);
  }

  clearUserData() {
    localStorage.clear();
    sessionStorage.clear();
    this.clearAllCookies();
  }

  clearAllCookies() {
    const allCookies = document.cookie.split(';');

    for (let i = 0; i < allCookies.length; i++) {
      const cookie = allCookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Setting the cookie with an expiry date in the past and specifying path and domain
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }

  getUserToken() {
    if (this.isGhostSession()) {
      const ghostToken = localStorage.getItem(this.ghostTokenObjectName);
      if (ghostToken) {
        return ghostToken;
      }
      return null;
    }

    const userType: string | null = this.getUserType();
    const fromStorage =
      localStorage.getItem(`fiGalaxyToken${userType}`) ||
      sessionStorage.getItem(`fiGalaxyToken${userType}`);
    if (fromStorage) {
      return fromStorage;
    }
    const fromCookie = this.getCookie(`fiGalaxyToken${userType}`);
    if (fromCookie && userType) {
      localStorage.setItem(`fiGalaxyToken${userType}`, fromCookie);
    }
    return fromCookie;
  }
  getUserObj(): any {
    if (this.isGhostSession()) {
      const ghostUser = localStorage.getItem(this.ghostUserObjectName);
      if (ghostUser) {
        return this.parseJson(ghostUser, null);
      }
      return null;
    }

    const userType: string | null = this.getUserType();
    const fromStorage = localStorage.getItem(`fiGalaxyObj${userType}`);
    if (fromStorage) {
      return this.parseJson(fromStorage, {});
    }
    const fromCookie = this.getCookie(`fiGalaxyObj${userType}`);
    if (fromCookie) {
      localStorage.setItem(`fiGalaxyObj${userType}`, fromCookie);
      return this.parseJson(fromCookie, {});
    }
    return {};
  }

  getPublicDomain() {
    return localStorage.getItem('publicDomain');
  }

  setPublicDomain(domain: string) {
    localStorage.setItem('publicDomain', domain);
  }

  getDomain() {
    const userSubDomain = this.getUserObj()?.sub_domain;
    const savedPublicDomain = this.getPublicDomain();
    let subDomainName;
    let subDomain = window.location.origin.split('://')[1].split('.')[0];
    if (
      window.location.origin.includes(`.${environment.domain}`) &&
      (subDomain != environment.centralDomain + '' ||
        !environment.centralDomain)
    ) {
      subDomainName = window.location.origin.split('://')[1].split('.')[0];
    }

    return userSubDomain || subDomainName || savedPublicDomain;
  }

  isUserAuth() {
    const userType: any = this.getUserType();
    if ((this.knownUserTypes as readonly string[]).includes(userType)) {
      return this.isAuth;
    }
    return false;
  }

  // cookies

  setCookie(name: string, value: any, days: number): void {
    // Cookie TTL is not a logout mechanism. Keep a long-lived mirror so
    // same-host cookie readers keep working; session validity is decided
    // by the backend (401).
    if (days > 0) {
      days = AuthService.SESSION_COOKIE_DAYS;
    }
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const encoded = encodeURIComponent(value == null ? '' : String(value));
    // Skip cookies that would exceed typical browser limits; localStorage
    // remains the source of the session.
    if (encoded.length > 3500) {
      return;
    }
    document.cookie = `${name}=${encoded};expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name: string): string | null {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      if (eqPos === -1) {
        continue;
      }
      const cookieName = cookie.substring(0, eqPos);
      if (cookieName !== name) {
        continue;
      }
      const cookieValue = cookie.substring(eqPos + 1);
      try {
        return decodeURIComponent(cookieValue);
      } catch {
        return cookieValue;
      }
    }
    return null;
  }

  deleteCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  private recoverUserTypeFromStoredToken(): string | null {
    for (const type of this.knownUserTypes) {
      if (localStorage.getItem(`fiGalaxyToken${type}`)) {
        localStorage.setItem('fi_galaxy_user_type', type);
        return type;
      }
    }
    return null;
  }

  private parseJson<T>(value: string, fallback: T): T {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
}
